'use strict';

(() => {
  const normalize = (v) => (v == null || v === '' ? '' : String(v));

  const buildVariantKey = (v) =>
    `${normalize(v.option1)}\t${normalize(v.option2)}\t${normalize(v.option3)}`;

  const buildOptionsKey = (opts) =>
    `${normalize(opts[0])}\t${normalize(opts[1])}\t${normalize(opts[2])}`;

  class VariantPickerComponent extends HTMLElement {
    constructor() {
      super();

      this.currentVariant = null;
      this.onInputChange = this.onInputChange.bind(this);
      this.onSoldOutLabelClick = this.onSoldOutLabelClick.bind(this);
    }

    connectedCallback() {
      const jsonEl = this.querySelector('[data-variant-picker-json]');
      if (!jsonEl?.textContent) return;

      try {
        this.data = JSON.parse(jsonEl.textContent);
      } catch {
        return;
      }

      if (!this.data?.variants?.length) return;

      this.variantMap = new Map();
      for (const variant of this.data.variants) {
        this.variantMap.set(buildVariantKey(variant), variant);
      }

      this.addEventListener('change', this.onInputChange);
      this.addEventListener('click', this.onSoldOutLabelClick);

      this.onVariantChange(null);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onInputChange);
      this.removeEventListener('click', this.onSoldOutLabelClick);

      this.variantMap = null;
      this.data = null;
      this.currentVariant = null;
    }

    getOptionsFromRadios() {
      const result = [];

      this.querySelectorAll('input[data-variant-picker-radio][data-variant-picker-index]').forEach(
        (input) => {
          const idx = Number(input.dataset.variantPickerIndex);
          if (!Number.isNaN(idx) && input.checked && !input.disabled) {
            result[idx] = input.value;
          }
        }
      );

      return result;
    }

    getOptionsFromSelects() {
      const result = [];

      this.querySelectorAll(
        'select[data-variant-picker-select][data-variant-picker-index]'
      ).forEach((select) => {
        const idx = Number(select.dataset.variantPickerIndex);
        if (!Number.isNaN(idx)) {
          result[idx] = select.value;
        }
      });

      return result;
    }

    setCurrentVariant(overrideInput = null) {
      const radios = this.getOptionsFromRadios();
      const selects = this.getOptionsFromSelects();

      const merged = [];
      const maxIdx = Math.max(radios.length - 1, selects.length - 1, 0);

      for (let i = 0; i <= maxIdx; i += 1) {
        merged[i] = selects[i] !== undefined ? selects[i] : radios[i];
      }

      if (overrideInput?.disabled && overrideInput.hasAttribute('data-variant-picker-radio')) {
        const idx = Number(overrideInput.dataset.variantPickerIndex);
        if (!Number.isNaN(idx)) {
          merged[idx] = overrideInput.value;
        }
      }

      let variant = this.variantMap.get(buildOptionsKey(merged)) || null;
      if (!variant) {
        variant =
          this.data.variants.find((v) =>
            merged.every((opt, i) => !opt || v[`option${i + 1}`] === opt)
          ) || null;
      }

      this.currentVariant = variant;

      return { optionValues: merged, variant };
    }

    syncRadioInputs(variant) {
      this.querySelectorAll('input[data-variant-picker-radio]').forEach((input) => {
        const idx = Number(input.dataset.variantPickerIndex);
        if (Number.isNaN(idx)) return;

        const optKey = `option${idx + 1}`;
        const isSelected = variant[optKey] === input.value;         

        if (!input.disabled) {
          input.checked = isSelected;
        } else if (isSelected) {
          this.querySelectorAll(`input[type="radio"][data-variant-picker-index="${idx}"]`).forEach(
            (r) => {
              if (r !== input) r.checked = false;
            }
          );
        }

        const label = input.id ? this.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;

        label?.classList.toggle('variant-picker__label--selected', isSelected);
      });
    }

    syncSelectInputs(variant) {
      this.querySelectorAll('select[data-variant-picker-select]').forEach((select) => {
        const idx = Number(select.dataset.variantPickerIndex);
        if (Number.isNaN(idx)) return;

        const val = variant[`option${idx + 1}`];
        if (val == null) return;

        const str = String(val);
        if (![...select.options].some((o) => o.value === str)) return;

        select.value = str;
      });
    }

    syncUI(variant) {
      this.syncRadioInputs(variant);
      this.syncSelectInputs(variant);

      const format =
        typeof _theme !== 'undefined' && typeof _theme.formatVariantPickerOptionLabel === 'function'
          ? _theme.formatVariantPickerOptionLabel
          : (v) => (v == null || v === '' ? '' : String(v));

      this.querySelectorAll('[data-variant-picker-label]').forEach((el, idx) => {
        const optVal = variant[`option${idx + 1}`];
        el.textContent = format(optVal);
      });
    }

    onInputChange(e) {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-variant-picker-index]')) return;
      if (target.tagName !== 'INPUT' && target.tagName !== 'SELECT') return;

      this.onVariantChange(null);
    }

    onSoldOutLabelClick(e) {
      const label = e.target.closest('.variant-picker__label--soldout');
      if (!label || !this.contains(label)) return;

      const forId = label.getAttribute('for');
      const input = forId
        ? this.querySelector(`#${CSS.escape(forId)}`)
        : label.querySelector('input[data-variant-picker-radio]');

      if (
        !input?.disabled ||
        !input.hasAttribute('data-variant-picker-radio') ||
        !this.contains(input)
      ) {
        return;
      }

      e.preventDefault();
      this.onVariantChange(input);
    }

    onVariantChange(overrideInput) {
      const { optionValues, variant } = this.setCurrentVariant(overrideInput);
      if (!variant) return;

      this.syncUI(variant);

      const dispatchEventDetail = {
        currentVariant: variant,
        currentVariantId: variant.id,
        selectedOptionValues: optionValues,
      };

      this.dispatchEvent(
        new CustomEvent('variant-picker-component:change', {
          bubbles: true,
          detail: dispatchEventDetail,
        })
      );
    }
  }
  if (!customElements.get('variant-picker-component')) {
    customElements.define('variant-picker-component', VariantPickerComponent);
  }
})();
